import { useGetStationDisplayName } from "@/entities/station";
import { Rijtijd } from "@/entities/tvv";
import { Icon } from "@/shared/components/ui";

type RouteTimelineProps = {
    stationCodes: string[];
    rijtijden?: Rijtijd[];
};

function findRijtijdForSegment(rijtijden: Rijtijd[], beginStation: string, eindStation: string): number | undefined {
    return rijtijden.find((r) => r.beginStation === beginStation && r.eindStation === eindStation)?.reisMinuten;
}

type TimelineLineProps = {
    visible?: boolean;
};

export function TimelineLine({ visible = true }: Readonly<TimelineLineProps>) {
    return visible ? <div className="bg-accent-blue-default w-0.5 flex-1" /> : <div className="flex-1" />;
}

type TimelineStationIndicatorProps = {
    variant: "endpoint" | "intermediate";
};

export function TimelineStationIndicator({ variant }: Readonly<TimelineStationIndicatorProps>) {
    if (variant === "endpoint") {
        return <div className="border-accent-blue-default z-10 h-4 w-4 shrink-0 rounded-full border-2 bg-white" />;
    }
    return <div className="bg-accent-blue-default ml-1.5 h-0.5 w-2" />;
}

type TimelineStationColumnProps = {
    position: "first" | "middle" | "last";
};

export function TimelineStationColumn({ position }: Readonly<TimelineStationColumnProps>) {
    return (
        <div className="flex w-3 flex-col items-center">
            <TimelineLine visible={position !== "first"} />
            <TimelineStationIndicator variant={position === "middle" ? "intermediate" : "endpoint"} />
            <TimelineLine visible={position !== "last"} />
        </div>
    );
}

export function RouteTimeline({ stationCodes, rijtijden }: Readonly<RouteTimelineProps>) {
    const getStationDisplayName = useGetStationDisplayName();
    const lastIndex = stationCodes.length - 1;

    return (
        <div className="flex flex-col" data-testid="route-stations-timeline">
            {stationCodes.map((code, index) => {
                const naam = getStationDisplayName(code);
                const isFirst = index === 0;
                const isLast = index === lastIndex;
                const nextCode = stationCodes[index + 1];
                const segmentRijtijd = rijtijden && nextCode ? findRijtijdForSegment(rijtijden, code, nextCode) : undefined;
                const position = isFirst ? "first" : isLast ? "last" : "middle";

                return (
                    <div key={code} className="relative flex items-stretch gap-3 py-4 pl-4" data-testid={code}>
                        <TimelineStationColumn position={position} />
                        <div className="flex flex-col pb-1">
                            <span className="text-content-emphasis text-s leading-tight">{naam}</span>
                        </div>
                        {!isLast && (
                            <div className="absolute bottom-[-12px] left-[-2px] flex items-center gap-2">
                                <span className="bg-accent-gray-subtle rounded-m min-w-12 px-2 py-1 text-center text-xs">
                                    {segmentRijtijd != null ? Rijtijd.formatSegmentRijtijd(segmentRijtijd) : "—"}
                                </span>
                                {segmentRijtijd == null && (
                                    <span className="text-content-subtle flex items-center gap-1 text-xs">
                                        <Icon name="alert" size="xs" />
                                        Rijtijd onbekend
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
